/* 生活方式故事滑块行为层
   桌面：section 拉高 + sticky 钉住，随页面滚动把轨道横向平移（rAF 节流 + lerp 惯性）
   移动 / reduced-motion：退出钉住，改用原生横向滚动
   以 Custom Element 承接 Theme Editor 生命周期，所有控件以自身为作用域查询 */

class LifestyleStorySlider extends HTMLElement {
  connectedCallback() {
    this.sticky = this.querySelector('.lifestyle-story-slider__sticky');
    this.track = this.querySelector('.lifestyle-story-slider__track');
    this.cards = Array.prototype.slice.call(this.querySelectorAll('.lifestyle-story-slider__card'));
    this.progressFill = this.querySelector('[data-slider-progress]');
    this.prevBtn = this.querySelector('[data-slider-previous]');
    this.nextBtn = this.querySelector('[data-slider-next]');
    this.loop = this.querySelector('[data-slider-loop="true"]') !== null;

    if (!this.track || !this.sticky || this.cards.length === 0) return;

    this.total = 0;
    this.maxX = 0;
    this.progress = 0;
    this.ticking = false;
    this.currentX = 0;
    this.targetX = 0;

    this.onTick = this.tick.bind(this);
    this.onRequestTick = this.requestTick.bind(this);
    this.onResize = this.resize.bind(this);
    this.onTrackScroll = this.trackScroll.bind(this);
    this.onModeChange = this.applyMode.bind(this);
    this.onPrev = this.goPrev.bind(this);
    this.onNext = this.goNext.bind(this);

    if (this.prevBtn) this.prevBtn.addEventListener('click', this.onPrev);
    if (this.nextBtn) this.nextBtn.addEventListener('click', this.onNext);
    this.track.addEventListener('scroll', this.onTrackScroll, { passive: true });
    window.addEventListener('scroll', this.onRequestTick, { passive: true });
    window.addEventListener('resize', this.onResize, { passive: true });
    window.addEventListener('load', this.onResize, { passive: true });

    // 断点与 CSS 的 min-width: 768px 保持一致
    this.desktopQuery = window.matchMedia('(min-width: 768px)');
    this.reduceQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (this.desktopQuery.addEventListener) this.desktopQuery.addEventListener('change', this.onModeChange);
    if (this.reduceQuery.addEventListener) this.reduceQuery.addEventListener('change', this.onModeChange);

    this.applyMode();
    this.updateProgress(0);
  }

  disconnectedCallback() {
    if (this.prevBtn) this.prevBtn.removeEventListener('click', this.onPrev);
    if (this.nextBtn) this.nextBtn.removeEventListener('click', this.onNext);
    if (this.track) this.track.removeEventListener('scroll', this.onTrackScroll);
    window.removeEventListener('scroll', this.onRequestTick);
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('load', this.onResize);
    if (this.desktopQuery && this.desktopQuery.removeEventListener) {
      this.desktopQuery.removeEventListener('change', this.onModeChange);
    }
    if (this.reduceQuery && this.reduceQuery.removeEventListener) {
      this.reduceQuery.removeEventListener('change', this.onModeChange);
    }
    this.ticking = false;
  }

  get pinned() {
    return this.classList.contains('lifestyle-story-slider__stage--pinned');
  }

  get reduceMotion() {
    return this.reduceQuery.matches;
  }

  applyMode() {
    if (this.desktopQuery.matches && this.cards.length > 1 && !this.reduceMotion) {
      this.enterPinned();
    } else {
      this.exitPinned();
    }
  }

  enterPinned() {
    if (this.pinned) return;
    this.classList.add('lifestyle-story-slider__stage--pinned');
    // 等钉住布局落定后再测量并绘制一次
    requestAnimationFrame(() => {
      this.measure();
      this.tick();
    });
  }

  exitPinned() {
    this.classList.remove('lifestyle-story-slider__stage--pinned');
    this.track.style.transform = '';
    this.currentX = 0;
    this.targetX = 0;
    this.trackScroll();
  }

  measure() {
    this.total = Math.max(0, this.offsetHeight - this.sticky.offsetHeight);
    this.maxX = Math.max(0, this.track.scrollWidth - this.sticky.clientWidth);
    this.currentX = this.targetX;
  }

  requestTick() {
    if (!this.pinned || this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(this.onTick);
  }

  tick() {
    this.ticking = false;
    if (!this.pinned) return;

    const rect = this.getBoundingClientRect();
    let p = this.total > 0 ? -rect.top / this.total : 0;
    p = Math.max(0, Math.min(1, p));
    this.progress = p;

    this.targetX = -(p * this.maxX);
    this.currentX += (this.targetX - this.currentX) * 0.12;
    this.track.style.transform = `translate3d(${this.currentX.toFixed(2)}px, 0, 0)`;

    this.updateProgress(p);

    if (Math.abs(this.targetX - this.currentX) > 0.5) {
      this.ticking = true;
      requestAnimationFrame(this.onTick);
    }
  }

  resize() {
    if (!this.pinned) return;
    this.measure();
    this.tick();
  }

  trackScroll() {
    if (this.pinned) return;
    const max = this.track.scrollWidth - this.track.clientWidth;
    this.updateProgress(max > 0 ? this.track.scrollLeft / max : 0);
  }

  currentIndex() {
    const n = this.cards.length;
    if (n < 2) return 0;
    if (this.pinned) return Math.round(this.progress * (n - 1));
    const max = this.track.scrollWidth - this.track.clientWidth;
    return max > 0 ? Math.round((this.track.scrollLeft / max) * (n - 1)) : 0;
  }

  goTo(index) {
    const n = this.cards.length;
    const behavior = this.reduceMotion ? 'auto' : 'smooth';

    if (this.loop && n > 1) {
      index = (index + n) % n;
    } else {
      index = Math.max(0, Math.min(index, n - 1));
    }

    if (this.pinned) {
      const p = n > 1 ? index / (n - 1) : 0;
      const stageTop = window.pageYOffset + this.getBoundingClientRect().top;
      window.scrollTo({ top: stageTop + p * this.total, behavior: behavior });
    } else {
      const card = this.cards[index];
      if (card) this.track.scrollTo({ left: card.offsetLeft, behavior: behavior });
    }
  }

  goPrev() {
    this.goTo(this.currentIndex() - 1);
  }

  goNext() {
    this.goTo(this.currentIndex() + 1);
  }

  updateProgress(p) {
    p = Math.max(0, Math.min(1, p));
    const n = this.cards.length || 1;

    if (this.progressFill) {
      const minPct = (1 / n) * 100;
      this.progressFill.style.width = `${minPct + p * (100 - minPct)}%`;
    }

    const idx = Math.round(p * (n - 1));
    const prevDisabled = !this.loop && idx <= 0;
    const nextDisabled = !this.loop && idx >= n - 1;

    if (this.prevBtn) {
      this.prevBtn.classList.toggle('is-disabled', prevDisabled);
      this.prevBtn.disabled = prevDisabled;
    }
    if (this.nextBtn) {
      this.nextBtn.classList.toggle('is-disabled', nextDisabled);
      this.nextBtn.disabled = nextDisabled;
    }
  }
}

if (!customElements.get('lifestyle-story-slider')) {
  customElements.define('lifestyle-story-slider', LifestyleStorySlider);
}
